import { Outlet, useNavigate } from "react-router-dom";
import { Menu, Users } from "lucide-react";

import { AppSidebar } from "@/components/layout/app-sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Branding } from "@/context/branding-context";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { useUser } from "@/context/user-provider";
import { ScrollArea } from "../ui/scroll-area";
import { usePermissionContext } from "@/context/permission-provider";

export default function AppShell() {
  const { user, logout } = useUser();
  const { isAdmin } = usePermissionContext();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  // Get user initials for the avatar fallback
  const userInitials = user?.name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase() || 'U';

  const handleNavigateToProfile = () => {
    navigate('/console/profile');
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="flex h-screen flex-col overflow-hidden">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
            {isMobile ? (
              <>
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="md:hidden">
                      <Menu className="h-5 w-5" />
                      <span className="sr-only">Toggle menu</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0">
                    <SheetHeader className="sr-only">
                      <SheetTitle>Navigation Menu</SheetTitle>
                      <SheetDescription>
                        Main navigation menu for accessing different sections of the application
                      </SheetDescription>
                    </SheetHeader>
                    <div className="flex h-full flex-col">
                      {/* Simple mobile navigation - not the full AppSidebar */}
                      <div className="p-4 border-b">
                        <div className="font-medium">{Branding.appName}</div>
                      </div>
                      <nav className="flex-1 overflow-y-auto p-2">
                        <ul className="space-y-1">
                          <li>
                            <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/console')}>
                              Dashboard
                            </Button>
                          </li>
                          <li>
                            <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/console/assets')}>
                              Assets
                            </Button>
                          </li>
                          <li>
                            
                          </li>
                          <li>
                            <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/console/kiosks')}>
                              Kiosks
                            </Button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </SheetContent>
                </Sheet>
              </>
            ) : (
              <>
              </>
            )}
            
            {/* User menu is added here, pushed to the right */}
            <div className="ml-auto flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="p-0 rounded-full overflow-hidden">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{userInitials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-normal">
                      <p className="font-semibold">{user?.name || "User"}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleNavigateToProfile}>
                    <Users className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {user && isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/console/admin')}>
                        <Users className="mr-2 h-4 w-4" />
                        <span>Admin Dashboard</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <div className="flex-1 overflow-auto">
            <ScrollArea className="h-[calc(100vh-3.5rem)] w-full">
              <div className="p-4 sm:p-6 md:p-8">
                <Outlet />
              </div>
            </ScrollArea>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}